import React, { useState, useRef } from 'react';

function FileUpload({ onFileUpload }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [analysisMode, setAnalysisMode] = useState('full'); // 'full', 'objectives_schedule', 'objectives_only'
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const maxFiles = 20;
    const validFiles = [];
    const invalidFiles = [];

    // Check if adding these files would exceed the limit
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed. You currently have ${selectedFiles.length} file(s) selected. You can only add ${maxFiles - selectedFiles.length} more file(s).`);
      return;
    }

    files.forEach(file => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`${file.name}: Invalid file type`);
        return;
      }

      // No file size limit - removed validation

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      alert('Some files were not added:\n' + invalidFiles.join('\n'));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (selectedFiles.length > 0) {
      onFileUpload(selectedFiles, analysisMode);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div
          className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragActive
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleChange}
            accept=".pdf,.doc,.docx"
            multiple
          />

          <svg
            className="mx-auto h-16 w-16 text-gray-400 mb-4"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {selectedFiles.length === 0 ? (
            <>
              <p className="text-lg font-medium text-gray-700 mb-2">
                Drop your syllabi here, or{' '}
                <button
                  onClick={handleButtonClick}
                  className="text-indigo-600 hover:text-indigo-700 underline"
                >
                  browse
                </button>
              </p>
              <p className="text-sm text-gray-500">
                Supports PDF and Word documents (no size limit)
              </p>
              <p className="text-sm text-indigo-600 font-medium mt-2">
                You can select multiple files at once (max 20 files)
              </p>
            </>
          ) : (
            <div className="w-full">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-8 w-8 text-green-500 mr-2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <p className="text-lg font-medium text-gray-700">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
                {selectedFiles.length < 20 && (
                  <button
                    onClick={handleButtonClick}
                    className="text-sm text-indigo-600 hover:text-indigo-700 underline"
                  >
                    + Add more ({20 - selectedFiles.length} remaining)
                  </button>
                )}
                {selectedFiles.length >= 10 && (
                  <span className="text-sm text-gray-500">
                    Maximum files reached
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="ml-3 text-red-600 hover:text-red-700 flex-shrink-0"
                      title="Remove file"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Analysis Mode</h3>
          <p className="text-xs text-gray-600 mb-3">Select which parts of the syllabus to analyze:</p>
          <div className="space-y-3">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="analysisMode"
                value="full"
                checked={analysisMode === 'full'}
                onChange={(e) => setAnalysisMode(e.target.value)}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">Full Syllabus Analysis</span>
                <p className="text-xs text-gray-600">Analyze the entire syllabus including all sections, topics, assignments, and schedule</p>
              </div>
            </label>

            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="analysisMode"
                value="objectives_schedule"
                checked={analysisMode === 'objectives_schedule'}
                onChange={(e) => setAnalysisMode(e.target.value)}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">Course Objectives + Schedule</span>
                <p className="text-xs text-gray-600">Analyze only the course objectives/learning outcomes and the course schedule/topic list</p>
              </div>
            </label>

            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="analysisMode"
                value="objectives_only"
                checked={analysisMode === 'objectives_only'}
                onChange={(e) => setAnalysisMode(e.target.value)}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">Course Objectives Only</span>
                <p className="text-xs text-gray-600">Analyze only the course objectives/learning outcomes section</p>
              </div>
            </label>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Analyze {selectedFiles.length} Syllab{selectedFiles.length !== 1 ? 'i' : 'us'}
            </button>
          </div>
        )}

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-2">How it works:</h3>
          <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
            <li>Upload one or more course syllabi (PDF or Word format)</li>
            <li>AI analyzes each document against AOL goals</li>
            <li>Review which goals are aligned with evidence quotes for each syllabus</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default FileUpload;
